import React, { useEffect, useState } from 'react';
import { CustomAutocomplete } from '_components/CustomAutocomplete';
import { useDispatch, useSelector } from 'react-redux';
import { emailsActions } from '_store';
import { MultiSelectAutocomplete } from '_components';
import { getIcons } from '_helpers';

export function EmailSelectionForm({ onEmailSelection, handleClose }) {

    // Récupération des emails depuis Redux
    const emails = useSelector(state => state.emails?.list);
    const dispatch = useDispatch();

    const [selectedFleet, setSelectedFleet] = useState(null);
    const [selectedClients, setSelectedClients] = useState([]);
    const [selectedTechs, setSelectedTechs] = useState([]);
    const [textareaValue, setTextareaValue] = useState('');

    const [ fleetOptions, setFleetOptions ] = useState([])
    const [ clientOptions, setClientOptions ] = useState([])
    const [ techOptions, setTechOptions ] = useState([])

    // Dispatch emails quand le composant est monté
    useEffect(() => {
        if (!emails) {
            dispatch(emailsActions.getAll());
        }
    }, [emails, dispatch]);

    // Met à jour les options des flottes quand emails change
    useEffect(() => {
        if (emails) {
            const fleets = Object.keys(emails).map(fleet => ({
                id: fleet,
                label: fleet
            }))
            setFleetOptions(fleets);
        }
    }, [emails]);

    // Met à jour les options des clients et techs quand une flotte est sélectionnée
    useEffect(() => {
        if (selectedFleet && emails) {
            const fleetData = emails[selectedFleet] || {};
            // Filtre les clients et techniciens pour exclure les valeurs vides
            const clients = fleetData.Clients?.filter(client => client && client !== '') || [];
            const szm = fleetData.SZM?.filter(tech => tech && tech !== '') || [];
            setClientOptions(clients);
            setTechOptions(szm);
        }
    }, [selectedFleet, emails]);

    // Gestion du changement de Flotte/concessionnaire
    const handleFleetChange = (newValue) => {
        setSelectedFleet(newValue); 
        setSelectedClients([]); 
        setSelectedTechs([]);
    };

    const handleClientChange = (newValue) => {
        setSelectedClients(newValue); // Mettez à jour la sélection des clients
    };

    const handleTechChange = (newValue) => {
        setSelectedTechs(newValue); // Mettez à jour la sélection des techs
    };

    const handleTextareaChange = (event) => {
        setTextareaValue(event.target.value);
    };

    // Fonction pour valider une adresse email avec une expression régulière
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = () => {
        // Récupére les valeurs sélectionnées
        const selectedData = {
            fleet: selectedFleet,
            clients: selectedClients,
            techs: selectedTechs
        };

        /* Récupére et traite les adresses email du textarea:
         La valeur du textarea contient les emails séparés par des virgules
         Il faut séparer les emails par des virgules,
         Supprimer les espaces vides avant et après chaque email
         et Garder uniquement les adresses email valides */
        const emailString = textareaValue; 
        const emailArray = emailString
            .split(',') 
            .map(email => email.trim()) 
            .filter(email => validateEmail(email)); 

        

        // Combine toutes les données en un seul objet
        const result = {
            fleet: selectedData.fleet,
            clientEmails: selectedData.clients, 
            techEmails: selectedData.techs,
            otherEmails: emailArray
        };

        onEmailSelection(result);
    };


    return (
        <>
        {emails ? (            
            <div className="mb-2">
                <div className="modal-subtitle">
                    Veuillez sélectionner les destinataires des alertes<br/>
                    <div className="modal-info">
                        Commencez par sélectionner une flotte pour afficher les emails correspondants
                    </div> 
                </div>          
                {/* Sélection de la flotte */}
                <div className="my-4">
                    <CustomAutocomplete
                        name="fleet"
                        label="Choisir une flotte"
                        options={fleetOptions}
                        noOptionsText="Aucune correspondance"
                        onChange={handleFleetChange}
                        onInputChange={() => false}
                        value={selectedFleet}
                        onClear={() => setSelectedFleet(null)}
                    />
                </div>   
                {/* Sélection des clients, visible seulement si une flotte est sélectionnée */}
                <div className="my-4">
                    {selectedFleet && (
                        <MultiSelectAutocomplete       
                            name="categories"                                      
                            label="Choisir des clients"
                            options={clientOptions}   
                            value={selectedClients}
                            onChange={handleClientChange}
                            onClear={() => setSelectedClients([])}
                            onInputChange={() => false}                                                           
                        />                        
                    )}
                </div>  
                {/* Sélection des techs, visible seulement si une flotte est sélectionnée */}          
                <div className="my-4">                    
                    {selectedFleet && (
                        <MultiSelectAutocomplete
                            name="techs"
                            label="Choisir des SZM"
                            options={techOptions}
                            value={selectedTechs}
                            onChange={handleTechChange}                    
                            onClear={() => setSelectedTechs([])}
                            onInputChange={() => false}  
                        />
                    )}
                </div> 
                {/* Champs de remplissage manuel des adresses email */}               
                <div className="modal-subtitle my-2">
                    Vous pouvez également ajouter des emails manuellement <br/>
                    <div className="modal-info">(en minuscule sans accents séparées par des virigules)</div>
                </div> 
                <textarea
                    className="form-control"
                    aria-label="With textarea"
                    value={textareaValue}
                    onChange={handleTextareaChange}
                ></textarea>

                {/* Boutons de validation */}
                <div className='flex justify-end mt-8 space-between'>
                    <button className='btn btn-custom btn-custom-secondary mr-4' onClick={handleClose}>
                        <img src={getIcons("clearOutlinedBlack")} alt="icon return" className="btn-custom-icon"/>
                        Annuler
                    </button>
                    <button className='btn btn-custom btn-custom-primary' onClick={handleSubmit}>
                        <img src={getIcons('deleteForeverOutlined')} alt="delete icon" className='btn-custom-icon' />
                        Valider
                    </button>
                </div>
            </div> 
        ) : (
            <>
                {/* Ecran de chargement en cas d'absence de données */}
                <div className='align-center flex justify-center h-36'>
                    <span className="dot-loader mr-2">Veuillez patienter, récupération des données en cours </span>
                    <span className="dot-loader">
                        <span className="dot">.</span>
                        <span className="dot">.</span>
                        <span className="dot">.</span>
                    </span>
                </div>
               
            </>
        )}
        </>
    );
}
